import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Group,
  Select,
  Stack,
  Text,
  Center,
  Loader,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import DealHierarchy from "../shared/DealHierarchy";

function RevenueTab() {
  const { getVerifiedJWT } = useAuth();
  const navigate = useNavigate();
  const [sellingYears, setSellingYears] = useState([]);
  const [selectedSellingYearId, setSelectedSellingYearId] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchSellingYears = useCallback(async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/selling-year/list"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const years = Array.isArray(result) ? result : result.data || [];
        setSellingYears(years);
        const activeYear = years.find(y => y.is_active);
        const defaultYear = activeYear || years[0];
        if (defaultYear) {
          setSelectedSellingYearId(defaultYear.id);
        }
      }
    } catch (err) {}
  }, [getVerifiedJWT]);

  const fetchRevenue = useCallback(async () => {
    if (!selectedSellingYearId) return;
    setLoading(true);
    setHasFetched(false);
    setRevenueData([]);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint(`/v1/pyr/${selectedSellingYearId}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setRevenueData(Array.isArray(result.data) ? result.data : []);
      }
    } catch (err) {
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, [getVerifiedJWT, selectedSellingYearId]);

  useEffect(() => {
    fetchSellingYears();
  }, [fetchSellingYears]);

  useEffect(() => {
    fetchRevenue();
  }, [fetchRevenue]);

  const noDeals = hasFetched && !loading && revenueData.length === 0;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center">
        <Group gap="xs" align="center">
          <Text size="sm" fw={500}>
            Selling Year
          </Text>
          <Select
            value={selectedSellingYearId ? String(selectedSellingYearId) : null}
            onChange={value => {
              if (!value) return;
              const match = sellingYears.find(y => String(y.id) === value);
              setSelectedSellingYearId(match ? match.id : null);
            }}
            data={sellingYears.map(y => ({
              value: String(y.id),
              label: y.name,
            }))}
            placeholder="Select selling year"
            allowDeselect={false}
            searchable={false}
            w={200}
          />
        </Group>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => navigate("/deals/add")}
          disabled={noDeals}
        >
          Add Deal
        </Button>
      </Group>

      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : noDeals ? (
        <Text size="sm" c="dimmed">
          You don't have a deal for this selling year. Please contact your
          manager.
        </Text>
      ) : (
        <DealHierarchy records={revenueData} />
      )}
    </Stack>
  );
}

export default RevenueTab;
